import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { fontStack } from '../styles/shared';

const PayoutRequestsQueue = ({ token }) => {
    const [requests, setRequests] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectId, setRejectId] = useState(null);
    const [txHashInput, setTxHashInput] = useState('');
    const [markSentId, setMarkSentId] = useState(null);
    const [error, setError] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/api/superadmin/payout-requests?status=${filter}`, { headers });
            setRequests(res.data.requests || []);
        } catch (e) { setError(e.response?.data?.error || 'Failed to load'); }
        finally { setLoading(false); }
    }, [filter, token]);

    useEffect(() => { load(); }, [load]);

    const handleApprove = async (id) => {
        setActionLoading(id);
        try {
            const res = await api.post('/api/superadmin/payout-requests/approve', { request_id: id }, { headers });
            if (res.data?.message) {
                alert(res.data.message);
            }
            load();
        } catch (e) { setError(e.response?.data?.error || 'Failed'); }
        finally { setActionLoading(null); }
    };

    const handleReject = async () => {
        if (!rejectId) return;
        setActionLoading(rejectId);
        try {
            await api.post('/api/superadmin/payout-requests/reject', { request_id: rejectId, reason: rejectReason }, { headers });
            setRejectId(null); setRejectReason('');
            load();
        } catch (e) { setError(e.response?.data?.error || 'Failed'); }
        finally { setActionLoading(null); }
    };

    const handleMarkSent = async () => {
        if (!markSentId || !txHashInput.trim()) return;
        setActionLoading(markSentId);
        try {
            await api.post('/api/superadmin/payout-requests/mark-sent', { request_id: markSentId, tx_hash: txHashInput.trim() }, { headers });
            setMarkSentId(null); setTxHashInput('');
            load();
        } catch (e) { setError(e.response?.data?.error || 'Failed'); }
        finally { setActionLoading(null); }
    };

    const maskWallet = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '—';

    const statusPill = (status) => {
        const c = { pending: ['#eab308', 'rgba(234,179,8,0.12)'], approved: ['#3b82f6', 'rgba(59,130,246,0.12)'], sent: ['#10b981', 'rgba(16,185,129,0.12)'], rejected: ['#ef4444', 'rgba(239,68,68,0.12)'] };
        const [col, bg] = c[status] || ['#888', '#f5f5f5'];
        return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: col, background: bg }}>{status}</span>;
    };

    const alertBadge = (text) => (
        <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, color: '#ef4444', background: 'rgba(239,68,68,0.1)', marginLeft: 4 }}>⚠ {text}</span>
    );

    return (
        <div style={{ fontFamily: fontStack }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Payout Requests</h3>
                <select value={filter} onChange={e => setFilter(e.target.value)} style={s.select}>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="sent">Sent</option>
                    <option value="rejected">Rejected</option>
                    <option value="all">All</option>
                </select>
            </div>

            {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {loading ? <div style={{ color: 'var(--text-secondary)' }}>Loading...</div> : requests.length === 0 ? (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>No {filter} payout requests</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Creator</th>
                                <th style={s.th}>Amount</th>
                                <th style={s.th}>Wallet</th>
                                <th style={s.th}>Status</th>
                                <th style={s.th}>Flags</th>
                                <th style={s.th}>Info</th>
                                <th style={s.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(r => (
                                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={s.td}>
                                        <div style={{ fontWeight: 600 }}>{r.admin_username}</div>
                                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                            {new Date(r.requested_at).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td style={{ ...s.td, fontWeight: 700, fontSize: 15 }}>${(r.amount || 0).toFixed(2)}</td>
                                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{maskWallet(r.wallet_address)}</td>
                                    <td style={s.td}>{statusPill(r.status)}</td>
                                    <td style={s.td}>
                                        {r.wallet_changed_recently && alertBadge('New wallet')}
                                        {r.chargeback_rate > 5 && alertBadge(`CB ${r.chargeback_rate}%`)}
                                        {r.amount > 500 && alertBadge('Large')}
                                    </td>
                                    <td style={{ ...s.td, fontSize: 11, color: 'var(--text-tertiary)' }}>
                                        <div>Prev payouts: ${(r.previous_payouts_total || 0).toFixed(0)}</div>
                                        <div>Reserve: ${(r.reserve_balance || 0).toFixed(0)}</div>
                                    </td>
                                    <td style={s.td}>
                                        {r.status === 'pending' && (
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                <button onClick={() => handleApprove(r.id)} disabled={actionLoading === r.id} style={s.approveBtn}>Approve</button>
                                                <button onClick={() => setRejectId(r.id)} style={s.rejectBtn}>Reject</button>
                                                <button onClick={() => setMarkSentId(r.id)} style={s.sentBtn}>Mark Sent</button>
                                            </div>
                                        )}
                                        {r.status === 'approved' && (
                                            <button onClick={() => setMarkSentId(r.id)} style={s.sentBtn}>Mark Sent</button>
                                        )}
                                        {r.status === 'sent' && r.tx_hash && (
                                            <a href={`https://bscscan.com/tx/${r.tx_hash}`} target="_blank" rel="noopener noreferrer"
                                               style={{ color: '#3b82f6', fontSize: 12 }}>View TX</a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Reject Modal */}
            {rejectId && (
                <div style={s.overlay} onClick={() => setRejectId(null)}>
                    <div style={s.modal} onClick={e => e.stopPropagation()}>
                        <h4 style={{ margin: '0 0 12px' }}>Reject Payout</h4>
                        <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                               placeholder="Reason (optional)" style={s.input} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button onClick={handleReject} style={s.rejectBtn}>Reject</button>
                            <button onClick={() => setRejectId(null)} style={s.cancelBtn}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mark Sent Modal */}
            {markSentId && (
                <div style={s.overlay} onClick={() => setMarkSentId(null)}>
                    <div style={s.modal} onClick={e => e.stopPropagation()}>
                        <h4 style={{ margin: '0 0 12px' }}>Mark as Sent</h4>
                        <input value={txHashInput} onChange={e => setTxHashInput(e.target.value)}
                               placeholder="BSC Transaction Hash (0x...)" style={{ ...s.input, fontFamily: 'monospace' }} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button onClick={handleMarkSent} disabled={!txHashInput.trim()} style={s.sentBtn}>Confirm Sent</button>
                            <button onClick={() => setMarkSentId(null)} style={s.cancelBtn}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const s = {
    select: { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border)', color: 'var(--text-tertiary)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' },
    td: { padding: '10px', verticalAlign: 'middle' },
    approveBtn: { padding: '4px 12px', borderRadius: 6, border: 'none', background: '#10b981', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    rejectBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    sentBtn: { padding: '4px 12px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    cancelBtn: { padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    modal: { background: 'var(--bg-primary)', borderRadius: 16, padding: 24, maxWidth: 420, width: '90%', border: '1px solid var(--border)' },
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
};

export default PayoutRequestsQueue;
